import React, { useState, useMemo } from 'react';
import { Search, Crown, MessageSquareQuote, CheckCircle, Clock, Ban, MapPin } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { resolveAssetUrl } from './../services/api';

/**
 * DriverBioWall — admin-only quick view of every driver's bio, the bio
 * counterpart to DriverPhotoWall. Search / filter / sort / pagination are all
 * client-side over the existing /admin/users response.
 *
 * Props:
 *   users: rows from getAllUsers() (non-driver rows are filtered out)
 *   onOpenDriver(driverId): opens the existing driver-details surface
 */
const PAGE_SIZE = 24;

const STATUS_META = {
  active: { label: 'Active', className: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: CheckCircle },
  pending_review: { label: 'Pending review', className: 'bg-amber-100 text-amber-800 border-amber-200', Icon: Clock },
  banned: { label: 'Banned', className: 'bg-red-100 text-red-700 border-red-200', Icon: Ban },
};

export default function DriverBioWall({ users = [], onOpenDriver }) {
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // all | has_bio | no_bio | premium | pending_review
  const [sortMode, setSortMode] = useState('newest');
  const [page, setPage] = useState(1);

  const drivers = useMemo(() => (users || []).filter(u => u.role === 'driver'), [users]);

  const visible = useMemo(() => {
    let rows = [...drivers];
    if (filterMode === 'has_bio') rows = rows.filter(u => (u.bio || '').trim());
    else if (filterMode === 'no_bio') rows = rows.filter(u => !(u.bio || '').trim());
    else if (filterMode === 'premium') rows = rows.filter(u => u.is_premium);
    else if (filterMode === 'pending_review') rows = rows.filter(u => u.compliance_status === 'pending_review');

    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(u =>
        (u.name || '').toLowerCase().includes(q)
        || (u.email || '').toLowerCase().includes(q)
        || (u.bio || '').toLowerCase().includes(q)
      );
    }

    const ts = (x) => (x ? new Date(x).getTime() || 0 : 0);
    if (sortMode === 'oldest') rows.sort((a, b) => ts(a.created_at) - ts(b.created_at));
    else if (sortMode === 'name') rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    else if (sortMode === 'jobs') rows.sort((a, b) => (b.jobs_completed || 0) - (a.jobs_completed || 0));
    else rows.sort((a, b) => ts(b.created_at) - ts(a.created_at));

    return rows;
  }, [drivers, filterMode, sortMode, search]);

  React.useEffect(() => { setPage(1); }, [filterMode, sortMode, search]);

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageRows = visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const withBio = drivers.filter(u => (u.bio || '').trim()).length;

  const FilterChip = ({ value, label }) => (
    <button
      type="button"
      onClick={() => setFilterMode(value)}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
        filterMode === value
          ? 'bg-[#2E7D32] text-white border-[#2E7D32]'
          : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
      }`}
      data-testid={`bio-wall-filter-${value}`}
    >
      {label}
    </button>
  );

  return (
    <div data-testid="driver-bio-wall">
      <div className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 mb-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search name, email or bio text…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
              data-testid="bio-wall-search"
            />
          </div>
          <select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            className="border border-gray-200 rounded-md px-2 h-9 text-xs bg-white"
            data-testid="bio-wall-sort"
          >
            <option value="newest">Newest joined</option>
            <option value="oldest">Oldest joined</option>
            <option value="name">Name A–Z</option>
            <option value="jobs">Most jobs completed</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="bio-wall-filters">
          <FilterChip value="all" label={`All drivers (${drivers.length})`} />
          <FilterChip value="has_bio" label={`With bio (${withBio})`} />
          <FilterChip value="no_bio" label={`No bio (${drivers.length - withBio})`} />
          <FilterChip value="premium" label="⭐ Premium" />
          <FilterChip value="pending_review" label="Pending review" />
        </div>
        <p className="text-xs text-gray-500" data-testid="bio-wall-count">
          Showing <span className="font-semibold text-gray-800">{pageRows.length}</span> of{' '}
          <span className="font-semibold text-gray-800">{visible.length}</span> drivers
        </p>
      </div>

      {visible.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-sm text-gray-500" data-testid="bio-wall-empty">
          No drivers match your filters.
        </div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="bio-wall-grid">
          {pageRows.map((d) => (
            <BioCard key={d.id} driver={d} onClick={() => onOpenDriver && onOpenDriver(d.id)} />
          ))}
        </ul>
      )}

      {totalPages > 1 && (
        <div className="mt-5 flex items-center justify-center gap-2 text-xs" data-testid="bio-wall-pagination">
          <Button type="button" variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="bio-wall-prev">Prev</Button>
          <span className="px-2 text-gray-600">Page {page} of {totalPages}</span>
          <Button type="button" variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="bio-wall-next">Next</Button>
        </div>
      )}
    </div>
  );
}

function BioCard({ driver, onClick }) {
  const status = driver.banned ? 'banned' : (driver.compliance_status || 'pending_review');
  const meta = STATUS_META[status] || STATUS_META.pending_review;
  const StatusIcon = meta.Icon;
  const photoUrl = driver.profile_image ? resolveAssetUrl(driver.profile_image) : null;
  const bio = (driver.bio || '').trim();
  const counties = driver.serves_all_ireland
    ? 'All Ireland'
    : Array.isArray(driver.counties) && driver.counties.length
      ? (driver.counties.length > 2 ? `${driver.counties.slice(0, 2).join(' · ')} (+${driver.counties.length - 2})` : driver.counties.join(' · '))
      : '—';

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="w-full h-full text-left bg-white border border-gray-200 rounded-xl p-3.5 hover:border-[#2E7D32] hover:shadow-md transition-all flex gap-3"
        data-testid={`bio-wall-card-${driver.id}`}
      >
        <span className="w-11 h-11 rounded-full bg-gray-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
          {photoUrl ? (
            <img src={photoUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <span className="text-xs font-bold text-gray-400">{(driver.name || '?').charAt(0).toUpperCase()}</span>
          )}
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-bold text-gray-900 truncate" data-testid={`bio-wall-name-${driver.id}`}>
              {driver.name || '—'}
            </span>
            {driver.is_premium && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-400 text-amber-950 text-[10px] font-bold">
                <Crown className="w-2.5 h-2.5" /> Premium
              </span>
            )}
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${meta.className}`}>
              <StatusIcon className="w-2.5 h-2.5" />
              {meta.label}
            </span>
          </span>

          <span className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-500">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{counties}</span>
          </span>

          {bio ? (
            <span
              className="mt-2 flex gap-1.5 text-sm text-gray-800 leading-snug"
              data-testid={`bio-wall-bio-${driver.id}`}
            >
              <MessageSquareQuote className="w-3.5 h-3.5 text-gray-300 flex-shrink-0 mt-0.5" />
              <span>“{bio}”</span>
            </span>
          ) : (
            <span className="mt-2 block text-sm text-gray-400 italic" data-testid={`bio-wall-nobio-${driver.id}`}>
              No bio added yet
            </span>
          )}
        </span>
      </button>
    </li>
  );
}
